import React, { useEffect } from 'react'
import { View } from 'react-native'
import { z } from 'zod'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { useTranslation } from 'react-i18next'
import { useOrderState } from '@order/hooks/useOrderState'
import { FormProvider, useForm } from 'react-hook-form'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInput from '@ui/forms/Input/FormInput'

interface Props {
  open: boolean
  close: () => void
}

export default function CommentModal({ open, close }: Props) {
  const { t } = useTranslation(['modals', 'ui'])
  const comment = useOrderState((s) => s.order.comment)
  const setComment = useOrderState((s) => s.setComment)

  const defaultValues = {
    comment: comment || '',
  }

  const resolver = zodResolver(
    z.object({
      comment: z.string().optional(),
    })
  )

  const form = useForm({ defaultValues, resolver })

  const isChanged = form.formState.isDirty
  const styleChanged = useAnimatedOpacity(isChanged)

  const onSubmit = form.handleSubmit((values) => {
    setComment(values.comment ?? null)
    close()
  })

  const onClose = () => {
    form.reset()
    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:comment.title')}
      desc={t('modals:comment.desc')}
      body={
        <FormProvider {...form}>
          <View className="gap-6 px-16">
            <FormInput
              control={form.control}
              name="comment"
              placeholder={t('ui:message')}
              className="h-36 items-start"
            />
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button variant="outlined" label={t('ui:close')} onPress={onClose} />
          {isChanged && (
            <Button
              variant="filled"
              label={t('ui:save')}
              iconEnd="arrow-narrow-right"
              onPress={onSubmit}
              style={styleChanged}
            />
          )}
        </>
      }
    />
  )
}
